import { TimePeriod } from 'app/model/CORE';
import TimeUtils from 'app/common/util/TimeUtils';
import moment from 'moment/moment';
import Utils from 'app/common/util/Utils';

/**
 * JS version of the methods defined on the java DTO TimePeiod class.
 */
export default class TimePeriodUtils {
    static COMPARE_FUNCTION = (tp1, tp2) => {
        if (tp1.from && tp2.from) return TimeUtils.compare(tp1.from, tp2.from);
        else if (tp1.from) return -1;
        else return 1;
    };

    /**
     * return true if the TimePeriod has been setup with 2 non-null dates,
     * whereby to >= from
     *
     */
    static isValid(tp: TimePeriod): boolean {
        return tp.from && tp.to && tp.to.getTime() >= tp.from.getTime();
    }

    /**
     * Return true if the two time period intersect
     */
    static intersectsDates(tp: TimePeriod, from: Date, to: Date): boolean {
        return to.getTime() > tp.from.getTime() && tp.to.getTime() > from.getTime();
    }

    /**
     * Returns true if the time period contains the date
     * Returns true if the two time period intersect
     */
    static intersects(tp: TimePeriod, tp2: TimePeriod | Date): boolean {
        return tp2 instanceof Date
            ? TimePeriodUtils.contains(tp, tp2)
            : TimePeriodUtils.intersectsDates(tp, tp2.from, tp2.to);
    }

    /**
     * Returns true if the date is contained within the time period [from, to[
     * Return true if the 2nd TimePeriod is contained within this TimePeriod.
     * @param tp : TimePeriod
     * @param arg: Date or timePeriod
     * @return
     */
    static contains(tp: TimePeriod, arg: Date | TimePeriod): boolean {
        if (arg instanceof Date) {
            const time = arg;
            return time.getTime() >= tp.from.getTime() && time.getTime() < tp.to.getTime();
        } else {
            const tp2 = arg;
            return TimePeriodUtils.containsDates(tp, tp2.from, tp2.to);
        }
    }

    /**
     * Returns true if the time is contained within [from, to]
     * i.e. this method breaks the normal convention of assuming the time period is [from, to[.
     */
    static containsInc(tp: TimePeriod, time: Date): boolean {
        return time.getTime() >= tp.from.getTime() && time.getTime() <= tp.to.getTime();
    }

    static containsDates(tp: TimePeriod, from: Date, to: Date): boolean {
        return tp.from.getTime() <= from.getTime() && tp.to.getTime() >= to.getTime();
    }

    /**
     * Returns the intersection of the two time period.
     * Note: this method should only be called after ensuring <code>intersects</code> is true.
     *
     */
    static intersection(tp1: TimePeriod, tp2: TimePeriod): TimePeriod {
        return { from: TimeUtils.max(tp1.from, tp2.from), to: TimeUtils.min(tp1.to, tp2.to) };
    }

    /**
     * Return true if the two timePeriods touch at their extremities.
     * @param tp
     * @return
     */
    static touches(tp1: TimePeriod, tp2: TimePeriod): boolean {
        return tp1.from.getTime() === tp2.to.getTime() || tp1.to.getTime() === tp2.from.getTime();
    }

    /**
     * Return a new TimePeriod that is shifted by the given amount
     * ** NOTE : THE ORIGINAL GWT/Java METHOD WAS NOT INVARIANT (i.e. it changed directly 'tp'). THIS IS NO LONGER THE CASE.
     * @param tp
     * @param shiftInMs
     */
    static shiftPeriod(tp: TimePeriod, shiftInMs): TimePeriod {
        return { from: new Date(tp.from.getTime() + shiftInMs), to: new Date(tp.to.getTime() + shiftInMs) };
    }

    /**
     * Return a new time period that is extended on both sides (from/to) by the given amounts.
     */
    static expandBy(tp: TimePeriod, expansionBeforeInMs: number, expansionAfterInMs: number): TimePeriod {
        return {
            from: new Date(tp.from.getTime() - expansionBeforeInMs),
            to: new Date(tp.to.getTime() + expansionAfterInMs),
        };
    }

    static getDurationInMs(tp: TimePeriod): number {
        return tp.to.getTime() - tp.from.getTime();
    }

    static copy(tp: TimePeriod): TimePeriod {
        return tp
            ? { from: tp.from ? new Date(tp.from.getTime()) : null, to: tp.to ? new Date(tp.to.getTime()) : null }
            : null;
    }

    static compareTo(tp1: TimePeriod, tp2: TimePeriod): number {
        if (tp1.from.getTime() < tp2.from.getTime()) return -1;
        if (tp1.from.getTime() > tp2.from.getTime()) return 1;

        if (tp1.to.getTime() < tp2.to.getTime()) return -1;
        if (tp1.to.getTime() > tp2.to.getTime()) return 1;

        return 0;
    }

    /**
     * Modifies 'periodToGrow' TimePeriod to cover the given periodOrDate.
     *
     */
    static growToCover(periodToGrow: TimePeriod, periodOrDate: Date | TimePeriod): void {
        if (periodOrDate instanceof Date) {
            const date: Date = periodOrDate;
            if (!periodToGrow.from || date.getTime() < periodToGrow.from.getTime()) {
                periodToGrow.from = new Date(date.getTime());
            }
            if (!periodToGrow.to || date.getTime() > periodToGrow.to.getTime()) {
                periodToGrow.to = new Date(date.getTime());
            }
        } else {
            const tp: TimePeriod = periodOrDate;
            if (!periodToGrow.from || tp.from.getTime() < periodToGrow.from.getTime()) {
                periodToGrow.from = new Date(tp.from.getTime());
            }
            if (!periodToGrow.to || tp.to?.getTime() > periodToGrow.to.getTime()) {
                periodToGrow.to = new Date(tp.to.getTime());
            }
        }
    }

    /**
     * Return the minimum TimePeriod that includes all of the given TimePeriods.
     *
     */
    static coversAll(periods: Array<TimePeriod>): TimePeriod {
        let reply = null;
        if (periods && periods.length > 0) {
            reply = TimePeriodUtils.copy(periods[0]);
            for (let i = 1; i < periods.length; i++) {
                TimePeriodUtils.growToCover(reply, periods[i]);
            }
        }
        return reply;
    }

    /**
     * Reduce the list but combining time periods that overlap / touch
     * @return
     */
    static sortAndCollapse(periods: TimePeriod[]): TimePeriod[] {
        const sortedPeriods = periods.slice().sort(TimePeriodUtils.COMPARE_FUNCTION);
        let reply = null;
        let i = 0;
        while (i < sortedPeriods.length) {
            const tp = sortedPeriods[i];
            let mergeIdx = -1;
            let mergedPeriod = tp;
            for (let j = i + 1; j < sortedPeriods.length; j++) {
                const tp2 = sortedPeriods[j];
                if (TimePeriodUtils.intersects(mergedPeriod, tp2)) {
                    if (mergeIdx === -1) {
                        // first time - so copy the data
                        mergedPeriod = TimePeriodUtils.copy(tp);
                    }
                    mergeIdx = j;
                    mergedPeriod.to = TimeUtils.max(mergedPeriod.to, tp2.to);
                } else {
                    break;
                }
            }
            if (mergeIdx === -1) {
                if (reply !== null) reply.push(mergedPeriod);
                i = i + 1;
            } else {
                if (reply === null) {
                    // time to create a new list
                    sortedPeriods.slice(0, i);
                    reply = sortedPeriods.slice(0, i);
                }
                reply.push(mergedPeriod);
                i = mergeIdx + 1;
            }
        }
        return reply ? reply : sortedPeriods;
    }

    /**
     * Merge 2 date-less times [from, to[ with a given day.
     * This merges day + times, but also performs some further checks
     *
     * If UNT is midnight, then assume day+1 midnight.
     *
     * The parsing will check if TO < FROM, and if so, increase FROM by 24 hours.
     *
     * If FROM = TO then we consider this to be an empty time period, which will generate an NM error.
     * This is a design choice : the other option would be the return a 24 hour period.
     * However, numerous FROM/TO periods are computed by algorithms, and injected into HMI query panels.
     * Therefore, in the case of a zero period computation, we wish to ensure this leads to an error, and not an
     * assumption of 24 hours.
     *
     * The only exception to this rule is [00:00, 00:00[ which is interpreted to be 24 hours.
     *
     * @param day
     * @param from
     * @param to
     */
    static mergeDateAndTimes(day: Date, from: Date, to: Date): TimePeriod {
        const tp = { from: TimeUtils.mergeDateAndTime(day, from), to: TimeUtils.mergeDateAndTime(day, to) };
        if (tp.to && TimeUtils.timeSinceMidnightInMs(tp.to) === 0) {
            tp.to = TimeUtils.addDays(tp.to, 1);
        }
        if (tp.from && tp.to && tp.to.getTime() < tp.from.getTime()) {
            tp.to = TimeUtils.addDays(tp.to, 1);
        }
        return tp;
    }

    static mergeDateAndTimePeriod(day: Date, tp: TimePeriod): TimePeriod {
        return this.mergeDateAndTimes(day, tp.from, tp.to);
    }

    /**
     * Subtracts a time period from another.
     * Result can be an array of 0, 1 or 2 time periods.
     *
     * @param timePeriod
     * @param tpToSubtract
     */
    static subtract(tp: TimePeriod, tpToSubtract: TimePeriod): Array<TimePeriod> {
        if (!tp) return null;

        // Nothing to substract to the period as it doesn't intersect period to be substracted to.
        if (!tpToSubtract || !TimePeriodUtils.intersects(tp, tpToSubtract)) return [tp];

        return [
            { from: tp.from, to: tpToSubtract.from },
            { from: tpToSubtract.to, to: tp.to },
        ].filter(tp => tp.to.getTime() > tp.from.getTime()); // Only keep valid periods (!= logic than TimePeriodUtils.isValid)
    }
    /**
     * Missing time values will default to
     *   WEF -> NOW
     *   UNT -> WEF + 2h
     *
     * @param wefUnt
     * @param day
     */
    static autoFillMissingValues(wefUnt: TimePeriod, day: Date): TimePeriod {
        if (wefUnt.from && wefUnt.to) return wefUnt;
        const tp: TimePeriod = { from: wefUnt.from, to: wefUnt.to };
        if (!tp.from) {
            tp.from = TimeUtils.mergeDateAndTime(day, TimeUtils.NOW());
        }
        if (!tp.to) {
            tp.to = TimeUtils.addMinutes(tp.from, 120);
        }
        return tp;
    }

    static autoFillMissingValuesWithDefaults(wefUnt: TimePeriod, defaultFrom: Date, defaultTo: Date): TimePeriod {
        if (wefUnt.from && wefUnt.to) return wefUnt;
        const tp: TimePeriod = { from: wefUnt.from, to: wefUnt.to };
        if (!tp.from) {
            tp.from = TimeUtils.mergeDateAndTime(defaultFrom, TimeUtils.NOW());
        }
        if (!tp.to) {
            tp.to = TimeUtils.mergeDateAndTime(defaultTo, TimeUtils.NOW());
        }
        return tp;
    }

    static formatHHMM(tp: TimePeriod): string {
        return TimeUtils.formatHHMM(tp.from) + ' - ' + TimeUtils.formatHHMM(tp.to);
    }

    static formatHH(tp: TimePeriod): string {
        return TimeUtils.formatHH(tp.from) + ' - ' + TimeUtils.formatHH(tp.to);
    }

    static formatDDHHMM(tp: TimePeriod): string {
        return TimeUtils.formatDDHHMM(tp.from) + ' - ' + TimeUtils.formatDDHHMM(tp.to);
    }

    static formatDateHHMM(tp: TimePeriod): string {
        return TimeUtils.formatDateHHMM(tp.from) + ' - ' + TimeUtils.formatDateHHMM(tp.to);
    }

    static timePeriodToMoment = (validity: TimePeriod) => {
        return [validity?.from && moment.utc(validity?.from), validity?.to && moment.utc(validity?.to)];
    };

    static momentToTimePeriod = (validity: moment.Moment[]) => ({
        from: validity[0]?.toDate(),
        to: validity[1]?.toDate(),
    });

    /**
     * Returns true if one of the TimePeriod intersects with another
     * @param timePeriods
     */
    static hasIntersections = (timePeriods: TimePeriod[]): boolean => {
        if (Utils.size(timePeriods) < 2) return false;

        const sorted = timePeriods.slice().sort(TimePeriodUtils.COMPARE_FUNCTION);

        for (let i = 1; i < sorted.length; i++) {
            if (TimePeriodUtils.intersects(sorted[i - 1], sorted[i])) return true;
        }

        return false;
    };

    static withoutSeconds(timePeriod: TimePeriod | undefined) {
        if (timePeriod) {
            const output: TimePeriod = {};
            if (timePeriod.from) {
                output.from = TimeUtils.withoutSeconds(timePeriod.from);
            }
            if (timePeriod.to) {
                output.to = TimeUtils.withoutSeconds(timePeriod.to);
            }
            return output;
        }
        return undefined;
    }

    static withoutMinutes(timePeriod: TimePeriod | undefined) {
        if (timePeriod) {
            const output: TimePeriod = {};
            if (timePeriod.from) {
                output.from = TimeUtils.withoutMinutes(timePeriod.from);
            }
            if (timePeriod.to) {
                output.to = TimeUtils.withoutMinutes(timePeriod.to);
            }
            return output;
        }
        return undefined;
    }
}
